// SELF — the faculties currently in play. Progressive reveal (handoff §8):
// only the tier the user is working on is foregrounded; secured lower tiers
// become quiet structure, higher tiers stay veiled.
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { normalizeMaladyId } from '../olmps/spectrum-tools';
import { ascensionRank, GEAR_TIERS, TIER_ORDER, type GearId, type TierId } from '../olmps/ascension';
import { computeProgress, todayKey, type ProgressStats } from '../olmps/deity-progress';
import { deityLevelData } from '../olmps/deity-data';
import { useCompassConfig } from '../olmps/sectorConfig';
import { DMT_TOOLS, type DmtToolId } from '../olmps/spectrum-tools';
import { GEAR_ORDER, SEAT_BY_ID, type Seat } from './roster';

export type SeatState = 'active' | 'secured' | 'veiled';

export interface SeatView {
  seat: Seat;
  stats: ProgressStats;
  state: SeatState;
  /** The malady holding the seat while the deity isn't secured. */
  maladyName: string;
  /** Malady subtype id (the intake-picked one if present, else the primary) — keys malady art. */
  maladyId: string;
  /** Whoever holds the seat right now: the deity once secured, else the malady. */
  holder: 'deity' | 'malady';
  /** SPECTRUM standing: −4..+4 displayed position (null = not started) and its state word. */
  standing: { position: number | null; label: string };
}

export interface SelfState {
  loading: boolean;
  rank: 'MORTAL' | 'DEMI' | 'TYTN' | 'OLMPN';
  securedCount: number;
  /** The tier currently being worked: the lowest tier not fully secured. */
  workingTier: TierId;
  /** The five foregrounded seats (one per gear, at the working tier). */
  active: SeatView[];
  /** Every seat with its state, for the full Mountain. */
  all: SeatView[];
  byGear: Record<GearId, SeatView>;
}

interface DmtRow {
  tool_id: string;
  displayed_position: number;
  acquisition_status: string;
  maintenance_status: string | null;
  malady_subtype: string | null;
}

export function useSelf(userId: string | undefined): SelfState {
  const { config, loading } = useCompassConfig(userId);
  const [rows, setRows] = useState<Record<string, DmtRow>>({});
  useEffect(() => {
    if (!userId) return;
    let live = true;
    supabase
      .from('dmt_state')
      .select('tool_id, displayed_position, acquisition_status, maintenance_status, malady_subtype')
      .eq('user_id', userId)
      .then(({ data }) => {
        if (!live || !data) return;
        setRows(Object.fromEntries((data as DmtRow[]).map((r) => [r.tool_id, r])));
      });
    return () => {
      live = false;
    };
  }, [userId]);

  return useMemo(() => {
    const today = todayKey();
    const statsOf = (id: DmtToolId) =>
      computeProgress(config.principleProgress?.[id], deityLevelData(id), today, id, { ledger: config.ledger, game: config.game });

    const statsMap = new Map<DmtToolId, ProgressStats>();
    for (const t of DMT_TOOLS) statsMap.set(t.id, statsOf(t.id));
    const securedCount = [...statsMap.values()].filter((s) => s.secured).length;

    const workingTier =
      TIER_ORDER.find((tier) => GEAR_ORDER.some((g) => !statsMap.get(GEAR_TIERS[g][tier])?.secured)) ?? 'olympian';
    const workingIdx = TIER_ORDER.indexOf(workingTier);

    const all: SeatView[] = GEAR_ORDER.flatMap((gear) =>
      TIER_ORDER.map((tier, idx) => {
        const id = GEAR_TIERS[gear][tier];
        const stats = statsMap.get(id)!;
        const tool = DMT_TOOLS.find((t) => t.id === id);
        // 'veiled' = not yet scored (cloud on the Mountain). It is a visual state
        // only — every tool is usable from day one (ruling 1.2).
        const state: SeatState = idx === workingIdx ? 'active' : idx < workingIdx || stats.secured ? 'secured' : 'veiled';
        const row = rows[id];
        const picked = row?.malady_subtype ? normalizeMaladyId(id, row.malady_subtype) : null;
        const mal = tool?.maladies.find((m) => m.id === picked) ?? tool?.maladies[0];
        const word = stats.secured
          ? ({ active: 'Secured', fading: 'Fading', at_risk: 'At Risk', dormant: 'Dormant' } as Record<string, string>)[row?.maintenance_status ?? 'active'] ?? 'Secured'
          : row
          ? ({ dormant: 'Dormant', training: 'Training', emerging: 'Emerging', stable: 'Stable' } as Record<string, string>)[row.acquisition_status] ?? 'Training'
          : 'Not started';
        return {
          seat: SEAT_BY_ID[id],
          stats,
          state,
          maladyName: mal?.name ?? '',
          maladyId: mal?.id ?? '',
          holder: stats.secured ? 'deity' : 'malady',
          standing: { position: row ? row.displayed_position : null, label: word },
        } as SeatView;
      }),
    );
    const active = all.filter((v) => v.state === 'active');
    const byGear = Object.fromEntries(active.map((v) => [v.seat.gear, v])) as Record<GearId, SeatView>;

    return { loading, rank: ascensionRank(securedCount), securedCount, workingTier, active, all, byGear };
  }, [config, loading, rows]);
}
